import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Loader2, Video, Mic, MicOff, VideoOff, Code, StickyNote } from 'lucide-react';
import Together from 'together-ai';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  type?: 'text' | 'code' | 'note';
  id?: string;
}

type InterviewType = 'technical' | 'behavioral' | 'system-design' | 'pair-programming';
type ExperienceLevel = 'junior' | 'mid' | 'senior';
type CompanyType = 'startup' | 'enterprise' | 'faang';

interface InterviewSimulatorProps {
  interviewType?: InterviewType;
  experienceLevel?: ExperienceLevel;
  companyType?: CompanyType;
  onClose?: () => void;
}

const client = new Together({
  apiKey: import.meta.env.VITE_TOGETHER_API_KEY,
});

export default function InterviewSimulator({
  interviewType: initialInterviewType = 'technical',
  experienceLevel: initialExperienceLevel = 'mid',
  companyType = 'enterprise'
}: InterviewSimulatorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [interviewType, setInterviewType] = useState<InterviewType>(initialInterviewType);
  const [experienceLevel, setExperienceLevel] = useState<ExperienceLevel>(initialExperienceLevel);
  const [showEditor, setShowEditor] = useState(false);
  const [editorType, setEditorType] = useState<'code' | 'note'>('code');
  const [editorContent, setEditorContent] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<Message[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      startInterview();
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const startInterview = async () => {
    setIsLoading(true);
    try {
      const prompt = `You are conducting a ${experienceLevel} level ${interviewType} interview for a ${companyType} company focusing on .NET/C# development.

Context:
- Interview Type: ${interviewType}
- Experience Level: ${experienceLevel}
- Company Type: ${companyType}

Start the interview with:
1. A brief introduction as the interviewer
2. Context about the role and company
3. Your first question based on the interview type

For Technical interviews: Focus on coding and architecture
For Behavioral: Focus on past experiences and scenarios
For System Design: Focus on scalability and design patterns
For Pair Programming: Present a collaborative coding challenge

Format your response using markdown with proper sections, code blocks, and formatting.`;

      const response = await client.chat.completions.create({
        model: 'meta-llama/Meta-Llama-3.1-405B-Instruct-Turbo',
        messages: [
          {
            role: 'system',
            content: 'You are an experienced technical interviewer for a software company. Conduct a professional and thorough technical interview.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.7
      });

      const introduction = response.choices[0]?.message?.content || 'Failed to start interview';
      setMessages([{ role: 'assistant', content: introduction }]);
    } catch (error) {
      console.error('Error starting interview:', error);
      setMessages([{ 
        role: 'assistant', 
        content: 'Technical difficulties. Please try again.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserResponse = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!userInput.trim() && pendingAttachments.length === 0) || isLoading) return;

    const allMessages = [
      ...pendingAttachments,
      ...(userInput.trim() ? [{ role: 'user', content: userInput, type: 'text' }] : [])
    ];

    setUserInput('');
    setPendingAttachments([]);
    setMessages(prev => [...prev, ...allMessages]);
    setIsLoading(true);

    try {
      const prompt = `Previous conversation: ${JSON.stringify(messages)}

User's response: ${allMessages.map(m => `[${m.type?.toUpperCase()}] ${m.content}`).join('\n')}

Evaluate the response and continue the interview. Consider:
1. Technical accuracy
2. Communication clarity
3. Problem-solving approach
4. Areas to probe further

Provide feedback if needed and ask your next question. Format your response using markdown.`;

      const response = await client.chat.completions.create({
        model: 'meta-llama/Meta-Llama-3.1-405B-Instruct-Turbo',
        messages: [
          {
            role: 'system',
            content: 'You are an experienced technical interviewer. Evaluate responses and continue the interview professionally.'
          },
          ...messages.map(m => ({
            role: m.role,
            content: m.content
          })),
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.7
      }); 

      const interviewerResponse = response.choices[0]?.message?.content || 'Failed to generate response';
      setMessages(prev => [...prev, { role: 'assistant', content: interviewerResponse }]);
    } catch (error) {
      console.error('Error in interview:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'I apologize, but I encountered an error. Shall we continue?' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditorConfirm = () => {
    if (editorContent.trim()) {
      const newMessage: Message = {
        role: 'user',
        content: editorContent,
        type: editorType,
        id: Math.random().toString(36).substr(2, 9)
      };
      setPendingAttachments(prev => [...prev, newMessage]);
      setEditorContent('');
      setShowEditor(false);
    }
  };

  const removeAttachment = (id: string) => {
    setPendingAttachments(prev => prev.filter(msg => msg.id !== id));
  };

  const renderMessage = (message: Message) => {
    if (message.type === 'code') {
      return (
        <div className="w-full max-w-2xl mx-auto relative group">
          {message.id && (
            <button
              onClick={() => removeAttachment(message.id)}
              className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 z-10 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <div className="bg-[#21252b] rounded-lg overflow-hidden shadow-lg">
            <div className="bg-[#21252b] px-3 py-2.5 flex items-center border-b border-gray-700">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-[#ff5f56] shadow-sm"></div>
                <div className="w-3 h-3 rounded-full bg-[#ffbd2e] shadow-sm"></div>
                <div className="w-3 h-3 rounded-full bg-[#27c93f] shadow-sm"></div>
              </div>
              <div className="absolute left-1/2 transform -translate-x-1/2 text-gray-400 text-sm font-mono">
                Program.cs
              </div>
            </div>
            <div className="p-4 font-mono text-sm">
              <SyntaxHighlighter
                style={vscDarkPlus as any}
                language="csharp"
                className="!bg-transparent"
              >
                {message.content}
              </SyntaxHighlighter>
            </div>
          </div>
        </div>
      );
    } else if (message.type === 'note') {
      return (
        <div className="w-full max-w-md mx-auto relative group">
          {message.id && (
            <button
              onClick={() => removeAttachment(message.id)}
              className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 z-10 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <div className="bg-gradient-to-br from-yellow-100 to-yellow-200 p-6 rounded-lg shadow-md transform rotate-1 font-kalam">
            <div className="whitespace-pre-wrap">{message.content}</div>
          </div>
        </div>
      );
    }
    
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          code({ node, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            return match ? (
              <SyntaxHighlighter
                style={vscDarkPlus as any}
                language={match[1]}
                PreTag="div"
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            ) : (
              <code className={className} {...props}>
                {children}
              </code>
            );
          }
        }}
      >
        {message.content}
      </ReactMarkdown>
    );
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 right-6 bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors group"
      >
        <div className="absolute inset-0 rounded-full bg-blue-400 animate-pulse opacity-75 blur-md group-hover:opacity-100" />
        <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 opacity-75 group-hover:opacity-100 blur animate-tilt" />
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 animate-pulse opacity-75 group-hover:opacity-100" />
        
        <Video className="w-6 h-6 relative z-10 transform group-hover:scale-110 transition-transform" />
      </button>

      {isOpen && (
        <div className="fixed inset-4 sm:inset-auto sm:bottom-24 sm:right-24 sm:w-[40rem] sm:h-[36rem] bg-white rounded-lg shadow-xl border border-gray-200">
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="font-semibold text-gray-800">Technical Interview Simulator</h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setVideoEnabled(!videoEnabled)}
                className={`p-2 rounded-lg ${videoEnabled ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
              >
                {videoEnabled ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setAudioEnabled(!audioEnabled)}
                className={`p-2 rounded-lg ${audioEnabled ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`}
              >
                {audioEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-4 border-b bg-gray-50">
            <div className="flex space-x-4">
              <select
                value={experienceLevel}
                onChange={(e) => setExperienceLevel(e.target.value as ExperienceLevel)}
                className="px-3 py-1 border rounded-lg"
              >
                <option value="junior">Junior Level</option>
                <option value="mid">Mid Level</option>
                <option value="senior">Senior Level</option>
              </select>
              <select
                value={interviewType}
                onChange={(e) => setInterviewType(e.target.value as InterviewType)}
                className="px-3 py-1 border rounded-lg"
              >
                <option value="technical">Technical</option>
                <option value="behavioral">Behavioral</option>
                <option value="system-design">System Design</option>
                <option value="pair-programming">Pair Programming</option>
              </select>
              <button
                onClick={startInterview}
                className="px-4 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Restart
              </button>
            </div>
          </div>

          <div className="h-[calc(100%-13rem)] overflow-y-auto p-4 space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'assistant' ? 'justify-start' : 'justify-end'}`}
              >
                <div
                  className={`max-w-[80%] p-4 rounded-lg ${
                    message.role === 'assistant'
                      ? 'bg-gray-100 text-gray-800'
                      : 'bg-blue-600 text-white'
                  }`}
                >
                  {renderMessage(message)}
                </div>
              </div>
            ))}
            {pendingAttachments.length > 0 && (
              <div className="space-y-4 border-l-4 border-blue-400 pl-4">
                <div className="text-sm text-gray-500">Pending attachments:</div>
                {pendingAttachments.map((attachment) => (
                  <div key={attachment.id} className="flex justify-start">
                    <div className="max-w-[80%]">
                      {renderMessage(attachment)}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 p-4 rounded-lg">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleUserResponse} className="p-4 border-t">
            <div className="flex gap-2">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setEditorType('code');
                    setShowEditor(true);
                  }}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Add Code"
                >
                  <Code className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditorType('note');
                    setShowEditor(true);
                  }}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Add Note"
                >
                  <StickyNote className="w-5 h-5" />
                </button>
              </div>
              <input
                type="text"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                placeholder={pendingAttachments.length > 0 ? "Add a message or send attachments..." : "Type your response..."}
                className="flex-1 p-2 border rounded-lg focus:outline-none focus:border-blue-500"
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={isLoading || (!userInput.trim() && pendingAttachments.length === 0)}
                className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-300"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </form>

          {showEditor && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl relative">
                <button
                  onClick={() => {
                    setShowEditor(false);
                    setEditorContent('');
                  }}
                  className="absolute -top-2 -right-2 bg-gray-800 text-white p-1 rounded-full hover:bg-gray-700 z-50 shadow-lg"
                >
                  <X className="w-4 h-4" />
                </button>
                {editorType === 'code' ? (
                  <>
                    <div className="bg-[#21252b] px-3 py-2.5 flex items-center border-b border-gray-700 rounded-t-lg">
                      <div className="flex gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-[#ff5f56] shadow-sm"></div>
                        <div className="w-3 h-3 rounded-full bg-[#ffbd2e] shadow-sm"></div>
                        <div className="w-3 h-3 rounded-full bg-[#27c93f] shadow-sm"></div>
                      </div>
                      <div className="absolute left-1/2 transform -translate-x-1/2 text-gray-400 text-sm font-mono">
                        Program.cs
                      </div>
                    </div>
                    <div className="bg-[#21252b] p-4 min-h-[400px]">
                      <textarea
                        value={editorContent}
                        onChange={(e) => setEditorContent(e.target.value)}
                        className="w-full h-full min-h-[400px] bg-transparent text-gray-300 font-mono resize-none focus:outline-none"
                        placeholder="Enter your code here..."
                        spellCheck={false}
                      />
                    </div>
                    <div className="flex justify-between items-center px-4 py-2 bg-[#21252b] border-t border-gray-700/50 text-xs font-mono rounded-b-lg">
                      <div className="text-gray-400">
                        <span className="text-blue-400">C#</span> · spaces: 4 · UTF-8
                      </div>
                      <button
                        onClick={() => {
                          handleEditorConfirm();
                          setUserInput(prev => prev + (prev ? '\n\n' : '') + '[Code snippet added]');
                        }}
                        className="flex items-center gap-1.5 text-gray-300 hover:text-white transition-colors group"
                      >
                        <span className="text-blue-400 group-hover:text-blue-300">{`public`}</span>
                        <span className="text-blue-400 group-hover:text-blue-300">{`void`}</span>
                        <span>{`Confirm`}</span>
                        <span className="text-purple-400 group-hover:text-purple-300">{`()`}</span>
                        <span className="text-yellow-300">{`{`}</span>
                        <span className="text-green-400 group-hover:text-green-300">{`Save();`}</span>
                        <span className="text-yellow-300">{`}`}</span>
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-gradient-to-br from-yellow-100 to-yellow-200 p-6 rounded-lg min-h-[300px] font-kalam relative">
                      <textarea
                        value={editorContent}
                        onChange={(e) => setEditorContent(e.target.value)}
                        className="w-full h-full min-h-[300px] bg-transparent resize-none focus:outline-none"
                        style={{
                          backgroundImage: 'repeating-linear-gradient(transparent, transparent 31px, #00000020 31px, #00000020 32px)'
                        }}
                        placeholder="Write your note here..."
                      />
                      <button
                        onClick={() => {
                          handleEditorConfirm();
                          setUserInput(prev => prev + (prev ? '\n\n' : '') + '[Note added]');
                        }}
                        className="absolute bottom-4 right-4 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-800 px-4 py-2 rounded-full transition-colors"
                      >
                        Confirm
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}